import {
    ModifyBudgetSkeleton,
} from '@/components/ModifyBudget';
import { WithLoader } from '@/components/WithLoader';
import { targetForBase } from '@/dashboards/BudgetOptimization/core/budgetSessionRules';
import { useBudgetOptimizationContext } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationContext';
import React, { useEffect, useRef } from 'react';
import { OptimisationBase } from './OptimisationBase';
import {
    OptimisationBudgetInput
} from './OptimisationBudgetInput';
import { Row } from './Row';
import { Header } from './Header';
import { Footer } from './Footer';

interface Props {
    onOutOfBoundsChannel?: (info: {
        channelName: string;
        min: number;
        max: number;
    }) => void;
    modalResetKey?: number;
}

export const ModifyBudget = ({
    onOutOfBoundsChannel,
    modalResetKey,
}: Props) => {
    const {
        data: { previous, optimized },
        onSubmit,
        recalculate,
        resetVariables,
        variableValues,
        disabledChannels,
        isLoading,
        isFetching,
        isOptimizing,
        optimizationBase,
        budget,
        setBudget,
        setTarget,
        committed,
        isDirty,
    } = useBudgetOptimizationContext();

    const originalLimitsRef: React.MutableRefObject<
        Record<string, { min: number; max: number; recommended: number }>
    > = useRef<
        Record<string, { min: number; max: number; recommended: number }>
    >({});

    useEffect(() => {
        if (optimized?.mediaLimits && optimized?.optimalVariables) {
            optimized.mediaLimits.forEach((limit) => {
                const isLocked = variableValues[limit.medium]?.locked;
                if (!(limit.medium in originalLimitsRef.current) || !isLocked) {
                    originalLimitsRef.current[limit.medium] = {
                        min: limit.lowerLimit || 0,
                        max: limit.upperLimit || 0,
                        recommended:
                            optimized.optimalVariables[limit.medium]
                                ?.optimalSpend || 0,
                    };
                }
            });
        }
    }, [optimized?.mediaLimits, optimized?.optimalVariables, variableValues]);

    const variablesByCategory =
        previous?.roiCurves.variablesAndCategories.byCategory || {};

    // Base and target change together, seeded from the committed run, so the input never
    // carries a value (or limits) from another base.
    const handleOptimizationBaseChange = (
        value: 'budget' | 'media_kpi' | 'total_kpi',
    ) => {
        setTarget(
            value,
            targetForBase(value, committed, previous?.totalContribution),
        );
    };

    const hasData = Object.keys(variablesByCategory).length > 0;
    const showSkeleton = !hasData && (isLoading || isFetching);

    if (showSkeleton) {
        return (
            <div className="p-3 glass-tile">
                <ModifyBudgetSkeleton />
            </div>
        );
    }

    return (
        <div className="p-3 glass-tile">
            <WithLoader
                className={isLoading ? 'min-h-96' : undefined}
                isLoading={isLoading}
                isFetching={isFetching}
                loadingMessage={false}
                delayedMessage={false}
            >
                <Header isDirty={isDirty} onClick={resetVariables} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-6 py-4 bg-white/1">
                    <div className="space-y-1">
                        <OptimisationBase
                            optimizationBase={optimizationBase}
                            handleOptimizationBaseChange={
                                handleOptimizationBaseChange
                            }
                        />
                    </div>
                    <div className="space-y-1">
                        <OptimisationBudgetInput
                            optimizationBase={optimizationBase}
                            budget={budget}
                            setEstimatedBudget={setBudget}
                            committed={committed}
                        />
                    </div>
                </div>
                <div className="px-3 ">
                    {Object.entries(variablesByCategory).map(
                        ([categoryName, variables], index) => {
                            const showSeparator =
                                index <
                                Object.keys(variablesByCategory).length - 1;

                            const enabledVariables = variables.filter(
                                (variable) =>
                                    !disabledChannels.includes(variable),
                            );

                            if (enabledVariables.length === 0) {
                                return null;
                            }

                            return (
                                <div key={categoryName}>
                                    <div className="flex group">
                                        <div className="w-8 flex items-center justify-center shrink-0 border-r border-white/5 group-hover:border-white/10 transition-colors">
                                            <div className="text-nowrap text-[10px] font-bold tracking-widest uppercase text-white/40 -rotate-90">
                                                {categoryName}
                                            </div>
                                        </div>
                                        <div className="grow space-y-1 py-1">
                                            {enabledVariables.map(
                                                (variable) => {
                                                    return (
                                                        <Row
                                                            key={`${categoryName}-${variable}`}
                                                            variable={variable}
                                                            modalResetKey={
                                                                modalResetKey
                                                            }
                                                            onOutOfBoundsChannel={
                                                                onOutOfBoundsChannel
                                                            }
                                                            onSubmit={onSubmit}
                                                            originalLimitsRef={
                                                                originalLimitsRef
                                                            }
                                                        />
                                                    );
                                                },
                                            )}
                                        </div>
                                    </div>
                                    {showSeparator && (
                                        <div className="h-px bg-linear-to-r from-transparent via-white/10 to-transparent my-2" />
                                    )}
                                </div>
                            );
                        },
                    )}
                </div>
                <Footer
                    dirty={isDirty}
                    onClick={recalculate}
                    loading={isOptimizing}
                />
            </WithLoader>
        </div>
    );
};
